import {
  AssetRoutine,
  AssetRoutineState,
  cleanUpDataChanges,
  FeatureName,
  Issue,
  IssueCreationSource,
  RoutineType,
  SAVE_ROUTINE_DRAFT,
  useCreateMutation,
  useCurrentAccount,
  useCurrentLocation,
  useCustomMutation,
  useIsFeatureEnabled,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';

import { Spacing } from 'libs/shared-ui/src/styles';
import { Colors } from '../../../themes/colors';
import { Button } from '../../crop-circle/Button';
import { SaveAsDraftIcon } from '../../crop-circle/icons';
import CustomAlert from '../../customAlert';
import { FormContext } from '../../forms';
import { KeyboardAwareScrollView } from '../../keyboard/keyboardAwareScrollView';
import {
  BottomComponent,
  BottomComponentBar,
  TopComponent,
  TopComponentCropCircle,
} from '../../navigation';
import { RoutineExecutionForm, RoutineModule } from '../types';
import { RoutineFormOld } from './routineFormOld';

export type RoutineFormProps = {
  workspaceAsset: WorkspaceAsset;
  module: RoutineModule<any, any>;
  routine?: WorkspaceRoutine;
  state?: AssetRoutineState<any>;
  additionalState?: any;
  onCompleted: (origin?: string) => Promise<void>;
  onCancel?: () => void;
  sourceIssue?: Issue;
  topBar: TopComponent;
  topBarCropCircle?: TopComponentCropCircle;
  rightButton?: ReactNode;
  preview?: boolean;
  footer?: ReactNode;
  createdFrom?: IssueCreationSource;
  isIssueEditRoutine?: boolean;
  assetRoutine?: AssetRoutine;
  onSaveDraft?: () => void;
  showSaveDraftButton?: boolean;
  draftData?: any;
  showNativeModal?: boolean;
  showConfirmBack?: boolean;
  bottomBar?: BottomComponent;
  /**
   * Called on mount with a guard function. Call that function with an `onProceed` callback
   * to check if the form has unsaved changes — if so, the confirm alert is shown and
   * `onProceed` is invoked only when the user confirms; otherwise `onProceed` is called immediately.
   */
  onRegisterBackGuard?: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
};

// GraphQL mutation will be implemented when backend is ready

export const RoutineForm = (props: RoutineFormProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <RoutineFormBase {...props} />;
  }

  return <RoutineFormOld {...props} />;
};

const RoutineFormBase = (props: RoutineFormProps) => {
  const { t } = useTranslation();
  const [isProcessing, toggleProcessing] = useState(false);
  const isProcessingRef = useRef(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const pendingProceedRef = useRef<(() => void) | null>(null);
  const pendingCancelRef = useRef<(() => void) | null>(null);
  const [isFromDraft, setIsFromDraft] = useState(false);
  const { mutateAsync, isPending } = useCreateMutation({
    query: CREATE_ASSET_ROUTINE,
    modelName: 'assetRoutine',
  });
  const { mutateAsync: updateAsync, isPending: isUpdating } = useCreateMutation(
    {
      query: UPDATE_ASSET_ROUTINE,
      modelName: 'assetRoutine',
    }
  );
  const { mutateAsync: saveDraftAsync } = useCustomMutation({
    query: SAVE_ROUTINE_DRAFT,
  });
  const { getLocation } = useCurrentLocation();
  const { account } = useCurrentAccount();

  const ExecutionForm: RoutineExecutionForm<any, any> | undefined = useMemo(
    () => props.module.executionForm,
    [props.module.executionForm]
  );
  const formOptions = useMemo(() => {
    const options: any = {
      mode: 'onChange',
    };
    const formResolver =
      props.module.getExecutionFormResolver &&
      props.module.getExecutionFormResolver(props.routine?.config, props.state);

    // Use draft data if available, otherwise use default values
    let defaultFormData;
    if (props.draftData && !props.isIssueEditRoutine) {
      defaultFormData = props.draftData;
    } else {
      defaultFormData =
        props.module.getExecutionFormDefaultValues &&
        props.module.getExecutionFormDefaultValues(
          props.routine?.config,
          props.state
        );
    }

    if (formResolver) {
      options.resolver = formResolver;
    }
    if (defaultFormData) {
      options.defaultValues = defaultFormData;
    }

    return options;
  }, [
    props.module,
    props.routine?.config,
    props.state,
    props.draftData,
    props.isIssueEditRoutine,
  ]);

  const formMethods = useForm(formOptions);

  // Register the back guard once on mount so external callers can intercept back actions
  useEffect(() => {
    props.onRegisterBackGuard?.((onProceed, onCancel) => {
      if (formMethods.formState.isDirty) {
        pendingProceedRef.current = onProceed;
        pendingCancelRef.current = onCancel ?? null;
        setConfirmCancel(true);
      } else {
        onProceed();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset form when draft data is loaded
  useEffect(() => {
    if (props.draftData && !props.isIssueEditRoutine) {
      setIsFromDraft(true);
      // Use setTimeout to ensure form is fully mounted before resetting
      setTimeout(() => {
        formMethods.reset(props.draftData, {
          keepDefaultValues: false,
        });

        // Manually set dirty state on all fields
        Object.keys(props.draftData).forEach((key) => {
          formMethods.setValue(key, props.draftData[key], {
            shouldDirty: true,
            shouldValidate: false,
          });
        });
      }, 100);
    }
  }, [props.draftData, formMethods, props.isIssueEditRoutine]);

  const onSaveDraft = async (data: any) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    toggleProcessing(true);
    try {
      const result = await saveDraftAsync({
        input: {
          workspaceAssetId: props.workspaceAsset.id,
          routineId: props.routine?.id,
          data: data,
        },
      });

      if (result?.routine?.saveRoutineDraft?.success) {
        // Call the parent's onSaveDraft callback if provided (don't await - let it run async)
        if (props.onSaveDraft) {
          props.onSaveDraft(); // Call without await so it doesn't block
        }
        // Close the form after saving draft
        props.onCancel && props.onCancel();
      } else {
        console.error(
          'Failed to save draft:',
          result?.routine?.saveRoutineDraft?.message
        );
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      isProcessingRef.current = false;
      toggleProcessing(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    toggleProcessing(true);

    if (props.module.transformSubmitData) {
      data = props.module.transformSubmitData(data);
    }

    if (props?.isIssueEditRoutine) {
      const cleanUpData = cleanUpDataChanges(
        props.assetRoutine?.data,
        data,
        props.routine?.type || RoutineType.Workflow,
        account?.id || ''
      );

      const input: any = {
        id: props.assetRoutine?.id,
        data: {
          ...cleanUpData,
        },
        updatedAt: new Date(),
      };

      if (props.sourceIssue) input.sourceIssue = { id: props.sourceIssue.id };

      await updateAsync(
        { input },
        {
          onSettled: async () => {
            try {
              await props.onCompleted('issue-edit');
            } finally {
              isProcessingRef.current = false;
              toggleProcessing(false);
            }
          },
        }
      );
      return;
    }

    const input: any = {
      asset: { id: props.workspaceAsset.asset.id },
      workspaceAsset: { id: props.workspaceAsset.id },
      workspace: { id: props.workspaceAsset.workspace.id },
      routine: { id: props.routine?.id },
      routineVersion: { id: props.routine?.currentVersion?.id },
      data: data,
      location: await getLocation(),
      ...(props.createdFrom ? { createdFrom: props.createdFrom } : {}),
    };
    if (props.sourceIssue) input.sourceIssue = { id: props.sourceIssue.id };

    await mutateAsync(
      { input },
      {
        onSettled: async () => {
          try {
            await props.onCompleted();
          } finally {
            isProcessingRef.current = false;
            toggleProcessing(false);
          }
        },
      }
    );
  };

  const canSave =
    formMethods.formState.isValid === false ||
    (!isFromDraft && formMethods.formState.isDirty === false) ||
    isPending === true ||
    isUpdating === true ||
    isProcessing === true;

  const BottomBar = props.bottomBar ?? BottomComponentBar;

  return (
    <View style={styles.routineFormContainer}>
      <FormContext.Provider value={formMethods}>
        {props.topBarCropCircle && (
          <props.topBarCropCircle
            title={props.routine?.config?.routineButtonText}
            rightNode={props.rightButton}
          />
        )}
        {ExecutionForm && (
          <KeyboardAwareScrollView
            containerStyle={styles.container}
            showsVerticalScrollIndicator={false}
            style={styles.scrollviewContainer}
          >
            <ExecutionForm
              config={props.routine?.config}
              currentState={props.state}
              workspaceAsset={props.workspaceAsset}
              toggleProcessing={toggleProcessing}
              isIssueEditRoutine={props.isIssueEditRoutine}
            />
          </KeyboardAwareScrollView>
        )}
        <BottomBar
          backButton={props.bottomBar ? undefined : <></>}
          style={props.bottomBar ? undefined : { marginHorizontal: 16 }}
          right={
            !props.preview &&
            props.showSaveDraftButton &&
            !props.isIssueEditRoutine ? (
              <Button
                variant="rounded"
                state="active"
                style={styles.saveDraftButton}
                onPress={() => {
                  const formData = formMethods.getValues();
                  onSaveDraft(formData);
                }}
                disabled={!formMethods.formState.isDirty || isProcessing}
                leftIcon={<SaveAsDraftIcon color={Colors.white} size={24} />}
              />
            ) : (
              <View style={{ marginRight: 16 }}></View>
            )
          }
          onSave={formMethods.handleSubmit(onSubmit)}
          saveLabel={
            props?.isIssueEditRoutine
              ? t('routines.resubmit')
              : t('routines.submit')
          }
          rightPressDisabled={canSave}
          onCancel={() => {
            setConfirmCancel(false);
            props.onCancel && props.onCancel();
          }}
          confirmBack={formMethods.formState.isDirty}
          showConfirmBack={
            props.showConfirmBack && formMethods.formState.isDirty
          }
          showAlert={() => setConfirmCancel(true)}
        />
      </FormContext.Provider>
      <CustomAlert
        visible={confirmCancel}
        primaryButtonLabel={t('routines.modalCancel.primary')}
        secondaryButtonLabel={t('routines.modalCancel.secondary')}
        tertiaryButtonLabel={
          props.showSaveDraftButton
            ? t('topNav.customAlert.saveProgress')
            : undefined
        }
        action={() => {
          const proceed = pendingProceedRef.current;
          pendingProceedRef.current = null;
          if (proceed) {
            proceed();
          } else {
            props.onCancel && props.onCancel();
          }
        }}
        tertiaryAction={async () => {
          const formData = formMethods.getValues();
          await onSaveDraft(formData);
        }}
        hideAlert={() => {
          const cancel = pendingCancelRef.current;
          pendingProceedRef.current = null;
          pendingCancelRef.current = null;
          setConfirmCancel(false);
          cancel?.();
        }}
        inline
      />
    </View>
  );
};

const CREATE_ASSET_ROUTINE = `
  mutation CreateAssetRoutine($input: CreateAssetRoutineInput!) {
    createAssetRoutine(input: $input) {
      id
    }
  }
`;

const UPDATE_ASSET_ROUTINE = `
  mutation UpdateAssetRoutine($input: MutateAssetRoutineInput!) {
    updateAssetRoutine(input: $input) {
      id
      data
      updatedAt
    }
  }
`;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: Colors.brand.white,
  },
  rowCropCircle: {
    justifyContent: 'flex-start',
    marginBottom: 80,
  },
  scrollviewContainer: {
    flex: 1,
    padding: Spacing.smallPadding.paddingHorizontal,
    paddingBottom: 160,
    ...Platform.select({
      web: {
        paddingBottom: 100,
      },
    }),
  },
  saveDraftButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginRight: 20,
  },
  saveDraftButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    textAlignVertical: 'center',
    color: Colors.black,
  },
  saveDraftDisabledButtonLabel: {
    color: Colors.grayDisabled,
  },
  routineFormContainer: {
    flex: 1,
    position: 'relative',
    backgroundColor: Colors.brand.white,
  },
});
